import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import ClockEarningsLogo from './ClockEarningsLogo';
import { IcoLock, IcoCheck } from '../dashboard/DashIcons';

export default function PrivatePage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail]     = useState('');
  const [status, setStatus]   = useState(null);
  const [message, setMessage] = useState('');

  const [showLogin, setShowLogin]         = useState(false);
  const [loginEmail, setLoginEmail]       = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError]       = useState('');
  const [loginLoading, setLoginLoading]   = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res  = await fetch('/api/newsletter/subscribe', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage(data.message || "You're on the waitlist!");
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Could not connect. Please try again.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const result = await login(loginEmail, loginPassword);
      if (result.success) {
        navigate('/');
      } else {
        setLoginError(result.error || 'Invalid credentials');
      }
    } catch {
      setLoginError('Could not connect. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '13px 16px',
    background: 'var(--ce-input-bg)',
    border: '1px solid var(--ce-border-accent)',
    borderRadius: 10,
    color: 'var(--ce-text)',
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 10,
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--ce-bg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
      padding: '24px',
      textAlign: 'center',
    }}>
      <div style={{ marginBottom: 40 }}>
        <ClockEarningsLogo height={80} />
      </div>

      <div style={{
        background: 'var(--ce-bg-card)',
        border: '1px solid var(--ce-border)',
        borderRadius: 20,
        padding: '48px 40px',
        maxWidth: 440,
        width: '100%',
      }}>
        {showLogin ? (
          <>
            <div style={{ marginBottom: 20 }}><IcoLock size={48} color="#374151"/></div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--ce-text)', margin: '0 0 8px', letterSpacing: '-0.5px' }}>
              Sign In
            </h1>
            <p style={{ fontSize: 14, color: 'var(--ce-text-soft)', margin: '0 0 28px' }}>
              For registered ClockEarnings organizations
            </p>

            <form onSubmit={handleLogin} style={{ textAlign: 'left' }}>
              <input
                type="email"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                placeholder="Email address"
                required
                autoFocus
                style={inputStyle}
              />
              <input
                type="password"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                placeholder="Password"
                required
                style={inputStyle}
              />
              {loginError && (
                <p style={{ margin: '0 0 10px', color: '#f87171', fontSize: 13, textAlign: 'center' }}>{loginError}</p>
              )}
              <button
                type="submit"
                disabled={loginLoading}
                style={{
                  width: '100%',
                  padding: '13px',
                  background: loginLoading ? 'rgba(34,197,94,0.4)' : 'linear-gradient(135deg, #0f4d2e, #22c55e)',
                  border: 'none',
                  borderRadius: 10,
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: loginLoading ? 'not-allowed' : 'pointer',
                  marginBottom: 12,
                }}
              >
                {loginLoading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>

            <button
              onClick={() => { setShowLogin(false); setLoginError(''); setLoginEmail(''); setLoginPassword(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--ce-text-muted)', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
            >
              ← Back
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 20 }}><IcoLock size={48} color="#374151"/></div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--ce-text)', margin: '0 0 12px', letterSpacing: '-0.5px' }}>
              Coming Soon
            </h1>
            <p style={{ fontSize: 15, color: 'var(--ce-text-soft)', margin: '0 0 32px', lineHeight: 1.65 }}>
              ClockEarnings is currently in private access. Join the waitlist and we'll let you know when we open up.
            </p>

            {status === 'success' ? (
              <div style={{
                padding: '16px 20px',
                background: 'var(--ce-accent-subtle)',
                border: '1px solid var(--ce-border-accent)',
                borderRadius: 10,
                color: 'var(--ce-accent)',
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 24,
              }}>
                <><IcoCheck size={14} color="#059669"/> {message}</>
              </div>
            ) : (
              <form onSubmit={handleWaitlist} style={{ marginBottom: 24 }}>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email to join the waitlist"
                  required
                  style={inputStyle}
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  style={{
                    width: '100%',
                    padding: '13px',
                    background: 'linear-gradient(135deg, #0f4d2e, #22c55e)',
                    border: 'none',
                    borderRadius: 10,
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                    opacity: status === 'loading' ? 0.7 : 1,
                  }}
                >
                  {status === 'loading' ? 'Joining…' : 'Join the Waitlist'}
                </button>
                {status === 'error' && (
                  <p style={{ margin: '8px 0 0', color: '#f87171', fontSize: 13 }}>{message}</p>
                )}
              </form>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--ce-border)' }} />
              <span style={{ fontSize: 12, color: 'var(--ce-text-muted)', fontWeight: 600 }}>OR</span>
              <div style={{ flex: 1, height: 1, background: 'var(--ce-border)' }} />
            </div>

            <button
              onClick={() => setShowLogin(true)}
              style={{
                width: '100%',
                padding: '13px',
                background: 'transparent',
                border: '1px solid var(--ce-border-accent)',
                borderRadius: 10,
                color: 'var(--ce-text-soft)',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: 20,
              }}
            >
              Sign in to existing account
            </button>

            <p style={{ fontSize: 13, color: 'var(--ce-text-muted)', margin: 0 }}>
              Questions?{' '}
              <a href="mailto:contact@flurrysystems.com" style={{ color: 'var(--ce-link)', textDecoration: 'none' }}>
                Contact us
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
